import { useRef, useState } from "react";

import { log } from "../log";
import { trpc } from "../trpc";
import type { Question, ReportReason } from "./types";

interface UseReportFlowReturn {
  /** Panel open state — drives the inline expand below the explanation. */
  open: boolean;
  setOpen: (v: boolean) => void;
  reason: ReportReason | null;
  setReason: (r: ReportReason | null) => void;
  comment: string;
  setComment: (v: string) => void;
  submitting: boolean;
  /** True once the report mutation has succeeded for this question. */
  sent: boolean;
  /**
   * True when the last submit failed — surfaces an inline "Couldn't
   * send — tap to retry" without a noisy banner. Cleared on the next
   * attempt and on reset.
   */
  failed: boolean;
  /** Submit the report for the given question; ignores if not ready. */
  submit: (current: Question | null) => Promise<void>;
  /** Reset state when advancing to the next question. */
  reset: () => void;
}

// Report-this-question state. Inline panel rather than a Modal so it
// renders consistently on web + native without platform Modal quirks.
// Failures are silent (console.warn) so a noisy banner doesn't derail
// the user mid-question.
export function useReportFlow(): UseReportFlowReturn {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [failed, setFailed] = useState(false);
  // The question id this report is being submitted for. If the user advances
  // mid-flight, reset() clears it and the late response no-ops instead of
  // flipping sent/failed for a question that's no longer on screen.
  const inFlightQuestionId = useRef<string | null>(null);

  function reset(): void {
    setOpen(false);
    setReason(null);
    setComment("");
    setSubmitting(false);
    setSent(false);
    setFailed(false);
    inFlightQuestionId.current = null;
  }

  async function submit(current: Question | null): Promise<void> {
    if (!current || !reason || submitting) return;
    const questionId = current.id;
    inFlightQuestionId.current = questionId;
    setSubmitting(true);
    setFailed(false);
    try {
      await trpc.questions.report.mutate({
        questionId,
        reason,
        comment: comment.trim() || undefined,
      });
      // Drop the result if the question changed under us (advance → reset).
      if (inFlightQuestionId.current !== questionId) return;
      setSent(true);
      setOpen(false);
    } catch (err) {
      log.warn({ err: String(err) }, "report failed");
      if (inFlightQuestionId.current !== questionId) return;
      setFailed(true);
    } finally {
      if (inFlightQuestionId.current === questionId) {
        setSubmitting(false);
      }
    }
  }

  return {
    open,
    setOpen,
    reason,
    setReason,
    comment,
    setComment,
    submitting,
    sent,
    failed,
    submit,
    reset,
  };
}
